import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Headphones, Plus } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { RecordCombobox } from '@/components/ui/record-combobox'
import { CreateContactModal } from '@/components/CreateContactModal'
import { useCreateTicket, useAccounts, useContacts, useProducts, useUsers } from '@/hooks/use-api'
import { useToast } from '@/hooks/use-toast'
import { useDebounce } from '@/hooks/use-debounce'
import { useUser } from '@/contexts/UserContext'
import { CATEGORICAL, GRACKLE_COLORS } from '@/lib/theme'
import type { TicketStatus, TicketPriority, TicketChannel } from '@/types'
import { explainError } from '@/lib/explain-error'

export default function CreateTicket() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { currentUser, canManageTickets } = useUser()
  const createMutation = useCreateTicket()

  // Read-only roles (Sales/User, Marketing, RFP) don't create tickets directly —
  // they use the Internal Support form. Redirect them away from this route.
  useEffect(() => {
    if (!canManageTickets) {
      toast({
        title: 'Not available',
        description: 'To raise a request, please use the Internal Support form.',
      })
      navigate('/tickets', { replace: true })
    }
  }, [canManageTickets, navigate, toast])

  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    status: 'New' as TicketStatus,
    priority: 'Medium' as TicketPriority,
    channel: 'manual' as TicketChannel,
    category: '',
    jira: '',
    account_id: '',
    contact_id: '',
    product_id: '',
    owner_id: currentUser?.id || '',
  })
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [createContactOpen, setCreateContactOpen] = useState(false)

  // Server-side search for dropdowns
  const [accountSearch, setAccountSearch] = useState('')
  const [contactSearch, setContactSearch] = useState('')
  const [productSearch, setProductSearch] = useState('')
  const debouncedAccountSearch = useDebounce(accountSearch, 300)
  const debouncedContactSearch = useDebounce(contactSearch, 300)
  const debouncedProductSearch = useDebounce(productSearch, 300)

  const { data: accountsData, isFetching: accountsLoading } = useAccounts({
    search: debouncedAccountSearch || undefined,
    limit: 10000,
    sort_by: 'name',
    sort_order: 'asc',
  })
  const { data: contactsData, isFetching: contactsLoading } = useContacts({
    search: debouncedContactSearch || undefined,
    limit: 10000,
    sort_by: 'last_name',
    sort_order: 'asc',
    account_id: formData.account_id || undefined,
  })
  const { data: productsData, isFetching: productsLoading } = useProducts({
    search: debouncedProductSearch || undefined,
    limit: 10000,
    sort_by: 'name',
    sort_order: 'asc',
  })
  const { data: usersData } = useUsers({ limit: 10000 })

  const accounts = accountsData?.data || []
  const contacts = contactsData?.data || []
  const products = productsData?.data || []
  const users = usersData?.data || []

  const handleFieldChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const errors: Record<string, string> = {}
    if (!formData.subject.trim()) errors.subject = 'Subject is required'
    if (!formData.owner_id) errors.owner_id = 'Owner is required'

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields.',
        variant: 'destructive',
      })
      return
    }

    try {
      const ticket = await createMutation.mutateAsync({
        ...formData,
        account_id: formData.account_id || undefined,
        contact_id: formData.contact_id || undefined,
        product_id: formData.product_id || undefined,
        category: formData.category || undefined,
        jira: formData.jira || undefined,
      })

      setFormErrors({})
      toast({
        title: 'Ticket Created',
        description: `GRK-${ticket.ticket_number} has been created`,
      })

      navigate(`/tickets/${ticket.id}`)
    } catch (error) {
      toast({
        title: 'Error',
        description: explainError(error, 'Failed to create ticket'),
        variant: 'destructive',
      })
    }
  }

  if (!canManageTickets) return null

  return (
    <div className="space-y-6" role="main" aria-label="Create new ticket">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/tickets')}
          aria-label="Back to tickets list"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        </Button>
        <Badge
          variant="outline"
          style={{
            backgroundColor: CATEGORICAL.plum.tint,
            color: GRACKLE_COLORS.prussianBlue,
            borderColor: CATEGORICAL.plum.solid,
          }}
        >
          <Headphones className="mr-1.5 h-3 w-3" aria-hidden="true" />
          Support
        </Badge>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">New Ticket</h1>
          <p className="text-sm text-muted-foreground mt-1">Create a new support ticket</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Ticket Details</CardTitle>
                <CardDescription>Enter the ticket information</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    id="subject"
                    value={formData.subject}
                    onChange={(e) => handleFieldChange('subject', e.target.value)}
                    placeholder="Brief description of the issue"
                    aria-required="true"
                    aria-invalid={!!formErrors.subject}
                    aria-describedby={formErrors.subject ? 'subject-error' : undefined}
                  />
                  {formErrors.subject && (
                    <p id="subject-error" className="text-sm text-destructive mt-1" role="alert">
                      {formErrors.subject}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => handleFieldChange('description', e.target.value)}
                    placeholder="Detailed description of the issue..."
                    rows={5}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(value) =>
                        setFormData({ ...formData, status: value as TicketStatus })
                      }
                    >
                      <SelectTrigger id="status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="New">New</SelectItem>
                        <SelectItem value="Open">Open</SelectItem>
                        <SelectItem value="Pending">Pending</SelectItem>
                        <SelectItem value="On Hold">On Hold</SelectItem>
                        <SelectItem value="Escalated">Escalated</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="priority">Priority</Label>
                    <Select
                      value={formData.priority}
                      onValueChange={(value) =>
                        setFormData({ ...formData, priority: value as TicketPriority })
                      }
                    >
                      <SelectTrigger id="priority">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Low">Low</SelectItem>
                        <SelectItem value="Medium">Medium</SelectItem>
                        <SelectItem value="High">High</SelectItem>
                        <SelectItem value="Urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="channel">Channel</Label>
                    <Select
                      value={formData.channel}
                      onValueChange={(value) =>
                        setFormData({ ...formData, channel: value as TicketChannel })
                      }
                    >
                      <SelectTrigger id="channel">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="manual">Manual</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="phone">Phone</SelectItem>
                        <SelectItem value="chat">Chat</SelectItem>
                        <SelectItem value="web">Web</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="category">Category</Label>
                    <Input
                      id="category"
                      value={formData.category}
                      onChange={(e) => handleFieldChange('category', e.target.value)}
                      placeholder="e.g., Technical Support"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="jira">Jira</Label>
                  <Input
                    id="jira"
                    value={formData.jira}
                    onChange={(e) => handleFieldChange('jira', e.target.value)}
                    placeholder="e.g., GRACKLE-123"
                    aria-label="Jira issue key"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Assignment</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="owner">Owner *</Label>
                  <Select
                    value={formData.owner_id}
                    onValueChange={(value) => handleFieldChange('owner_id', value)}
                  >
                    <SelectTrigger
                      id="owner"
                      aria-required="true"
                      aria-invalid={!!formErrors.owner_id}
                      aria-describedby={formErrors.owner_id ? 'owner-error' : undefined}
                    >
                      <SelectValue placeholder="Select owner" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formErrors.owner_id && (
                    <p id="owner-error" className="text-sm text-destructive mt-1" role="alert">
                      {formErrors.owner_id}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Related Records</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="account">Account</Label>
                  <RecordCombobox
                    id="account"
                    records={accounts}
                    value={formData.account_id}
                    onValueChange={(value) => {
                      setFormData({
                        ...formData,
                        account_id: value,
                        contact_id: '', // Clear contact when account changes
                      })
                      setContactSearch('')
                    }}
                    placeholder="Select account"
                    searchPlaceholder="Search accounts..."
                    emptyMessage="No accounts found."
                    displayField={(acc) => acc.name || acc.account_name || ''}
                    aria-label="Account"
                    onSearchChange={setAccountSearch}
                    isLoading={accountsLoading}
                    totalCount={accountsData?.total}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact">Contact</Label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <RecordCombobox
                        id="contact"
                        records={contacts}
                        value={formData.contact_id}
                        onValueChange={(value) => setFormData({ ...formData, contact_id: value })}
                        placeholder="Select contact"
                        searchPlaceholder="Search contacts..."
                        emptyMessage="No contacts found."
                        displayField={(c) => `${c.first_name || ''} ${c.last_name || ''}`.trim()}
                        aria-label="Contact"
                        onSearchChange={setContactSearch}
                        isLoading={contactsLoading}
                        totalCount={contactsData?.total}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="shrink-0"
                      onClick={() => setCreateContactOpen(true)}
                      aria-label="Create new contact"
                    >
                      <Plus className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="product">Product</Label>
                  <RecordCombobox
                    id="product"
                    records={products}
                    value={formData.product_id}
                    onValueChange={(value) => setFormData({ ...formData, product_id: value })}
                    placeholder="Select product"
                    searchPlaceholder="Search products..."
                    emptyMessage="No products found."
                    displayField={(p) => p.name || ''}
                    aria-label="Product"
                    onSearchChange={setProductSearch}
                    isLoading={productsLoading}
                    totalCount={productsData?.total}
                  />
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => navigate('/tickets')}
              >
                Cancel
              </Button>
              <Button type="submit" className="flex-1" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Creating...' : 'Create Ticket'}
              </Button>
            </div>
          </div>
        </div>
      </form>

      <CreateContactModal
        open={createContactOpen}
        onOpenChange={setCreateContactOpen}
        accountId={formData.account_id}
        ownerId={currentUser?.id}
        onCreated={(contact) => {
          setFormData((prev) => ({ ...prev, contact_id: contact.id }))
        }}
      />
    </div>
  )
}
